import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type { Repair } from '@atlassian/adf-parser';
import type { GenerateImageProps } from '@atlassian/ai-image-generation';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type {
	ConfluenceCreatePageCommandProperties,
	ConfluenceDeepResearchPageCreateCommandProperties,
	ConfluencePageInitCommandProperties,
	ConfluenceUpdatePageCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands';

export type StreamType = 'create' | 'deep-research-create' | 'update' | 'init';

export type StreamCommandProperties =
	| ConfluenceCreatePageCommandProperties
	| ConfluenceDeepResearchPageCreateCommandProperties
	| ConfluenceUpdatePageCommandProperties
	| ConfluencePageInitCommandProperties;

/**
 * Parameters passed to stream start callbacks.
 * Includes the stream type and required command properties when streaming begins.
 */
export type StreamStartCallbackParams = { streamType: StreamType; params: StreamCommandProperties };

/**
 * Callback function invoked when a streaming operation begins.
 * This allows listeners to prepare for incoming stream data and set up any necessary state.
 *
 * @param params - Object containing the stream type and command properties
 */
export type StreamStartCallback = (params: StreamStartCallbackParams) => void;

/**
 * Parameters passed to stream callbacks during active streaming.
 * Contains the stream type and current command properties with streaming data.
 */
export type StreamCallbackParams = { streamType: StreamType; params: StreamCommandProperties };

/**
 * Callback function invoked during active streaming operations.
 * Called repeatedly as stream data arrives to allow real-time processing and UI updates.
 *
 * @param params - Object containing the stream type and current command properties
 */
export type StreamCallback = (params: StreamCallbackParams) => void;

/**
 * Parameters passed to stream end callbacks when streaming completes.
 * Contains the stream type and command properties for cleanup operations.
 */
export type StreamEndCallbackParams = {
	streamType: StreamType;
	params: StreamCommandProperties;
};

/**
 * Callback function invoked when a streaming operation completes successfully.
 * Used to finalize processing, clean up resources, and update the UI to reflect completion.
 *
 * @param params - Object containing the stream type and final command properties
 */
export type StreamEndCallback = (params: StreamEndCallbackParams) => void;

/**
 * Parameters passed to error callbacks when streaming operations fail.
 * Contains error information and the stream type for context.
 */
export type ErrorCallbackParams = { error: any; streamType: StreamType };

/**
 * Callback function invoked when a streaming operation encounters an error.
 * Used to handle failures, display error messages, and clean up resources.
 *
 * @param params - Object containing the error and stream type for context
 */
export type ErrorCallback = (params: ErrorCallbackParams) => void;

/**
 * Parameters passed to repair callback when repairs are made during streaming.
 */
export type RepairCallbackParams = { streamType: StreamType; repairs: Repair[] };

/**
 * Callback function invoked when repairs are made during streaming operations.
 */
export type RepairCallback = (params: RepairCallbackParams) => void;

/**
 * Properties passed through to command handlers from the command manager.
 */
export type PassThroughCommandProperties = {
	editorAPI: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	contentId: string;
	onStreamStart: StreamStartCallback;
	onStream: StreamCallback;
	onStreamEnd: StreamEndCallback;
	onStreamError: ErrorCallback;
	onStreamRepair: RepairCallback;
	forceDraftSync: (() => Promise<DocNode>) | undefined;
	generateAIHeaderImage: ((props: GenerateImageProps) => Promise<void>) | undefined;
	setEditorDisabled: (disabled: boolean) => void;
	reuploadImages: ((adfDoc: DocNode) => Promise<void>) | undefined;
	sendExperienceSuccess: (() => void) | undefined;
	isViewMode?: boolean;
};

/**
 * Base interface for all command handlers in the Rovo bridge system.
 * Command handlers are responsible for processing commands received from the Rovo bridge
 * and executing the appropriate operations in the editor.
 */
export interface ICommandHandler {
	/**
	 * Listens for and processes a command with the provided parameters.
	 * This is the main entry point for command execution.
	 *
	 * @param params - Command-specific parameters to process
	 * @returns Promise or direct result depending on the command type
	 */
	listen(params: any): any;
}

/**
 * Interface for command handlers that support streaming operations.
 * Streaming handlers can be aborted mid-execution to stop processing
 * and clean up resources.
 */
export interface IStreamingCommandHandler {
	/**
	 * Aborts the current streaming operation.
	 * This should immediately stop processing, clean up any resources,
	 * and reset the handler to a ready state.
	 */
	abort(): void;
}

/**
 * Abstract base class for all command handlers in the Rovo bridge system.
 * Provides common functionality and structure for handling commands from the Rovo bridge.
 *
 * @template TCommandProperties - The type of properties/parameters this handler expects
 * @template TCommandResult - The type of result this handler returns
 */
export abstract class CommandHandler<TCommandProperties, TCommandResult>
	implements ICommandHandler
{
	/**
	 * Properties passed through from the command manager containing callbacks
	 * and utility functions for interacting with the editor and streaming system.
	 */
	protected properties: PassThroughCommandProperties;

	/**
	 * Creates a new command handler instance.
	 *
	 * @param properties - Pass-through properties containing callbacks and utilities
	 *                    needed for command execution and streaming operations
	 */
	constructor(properties: PassThroughCommandProperties) {
		this.properties = properties;
	}

	/**
	 * Abstract method that must be implemented by concrete command handlers.
	 * This method contains the core logic for processing the specific command type.
	 *
	 * @param params - Command-specific parameters containing the data needed for execution
	 * @returns Promise that resolves with the command result when processing is complete
	 */
	abstract listen(params: TCommandProperties): Promise<TCommandResult>;
}
